import React, { useId, useRef, useState } from "react";

import "../../styles/field.scss";
import "./FileInput.scss";

export type FileKind = "pdf" | "image" | "doc";

interface KindSpec {
  /** Goes into the input's accept attribute. */
  accept: string;
  /** Shown under the file name once one is picked. */
  label: string;
  /** Badge text on the icon tile. */
  badge: string;
  matches: (file: File) => boolean;
}

const KINDS: Record<FileKind, KindSpec> = {
  pdf: {
    accept: ".pdf,application/pdf",
    label: "PDF",
    badge: "PDF",
    matches: (file) =>
      file.type === "application/pdf" || /\.pdf$/i.test(file.name),
  },
  image: {
    accept: "image/*",
    label: "Image",
    badge: "IMG",
    matches: (file) =>
      file.type.startsWith("image/") ||
      /\.(png|jpe?g|gif|webp|heic|heif|bmp)$/i.test(file.name),
  },
  doc: {
    accept:
      ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    label: "Document",
    badge: "DOC",
    matches: (file) =>
      /\.docx?$/i.test(file.name) || file.type.startsWith("application/msword"),
  },
};

const kindOf = (file: File, allow: FileKind[]): FileKind | null =>
  allow.find((kind) => KINDS[kind].matches(file)) || null;

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileIcon: React.FC<{ badge: string }> = ({ badge }) => (
  <span className="fi-icon">
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M14 2.5H7a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7.5l-5-5Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M14 2.5v5h5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
    <span className="fi-icon-badge">{badge}</span>
  </span>
);

interface Props {
  label?: React.ReactNode;
  /** Which kinds the picker offers and accepts on drop. */
  allow?: FileKind[];
  /** Fires with every accepted file. Empty array when the last one is cleared. */
  onSelect?: (files: File[]) => void;
  multiple?: boolean;
  /** Colours the label, the box and the helper text. */
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  /** Text on the empty-state control. */
  buttonText?: string;
  className?: string;
}

const FileInput: React.FC<Props> = ({
  label,
  allow = ["pdf", "image", "doc"],
  onSelect,
  multiple = false,
  error = false,
  helperText,
  disabled = false,
  required = false,
  buttonText = "choose file",
  className = "",
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  // Drag and drop bypasses the accept attribute entirely, so a rejection can
  // only be reported after the fact.
  const [rejected, setRejected] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const generatedId = useId();
  const helperId = `${generatedId}-helper`;

  const accept = allow.map((kind) => KINDS[kind].accept).join(",");
  const hint = allow.map((kind) => KINDS[kind].label).join(", ");

  const commit = (incoming: File[]) => {
    const accepted = incoming.filter((file) => kindOf(file, allow));

    if (accepted.length < incoming.length) {
      setRejected(`Only ${hint} files are accepted.`);
    } else {
      setRejected(null);
    }
    if (accepted.length === 0) return;

    const next = multiple ? [...files, ...accepted] : accepted.slice(0, 1);
    setFiles(next);
    onSelect?.(next);
  };

  const remove = (index: number) => {
    const next = files.filter((_, i) => i !== index);
    setFiles(next);
    setRejected(null);
    onSelect?.(next);
    // Without this the same file picked again fires no change event.
    if (inputRef.current) inputRef.current.value = "";
  };

  const showEmptyState = files.length === 0 || multiple;
  const message = rejected || helperText;

  return (
    <div
      className={[
        "fld",
        "fi",
        error || rejected ? "fld-error" : "",
        disabled ? "fld-disabled" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label && (
        <label className="fld-label" htmlFor={generatedId}>
          {label}
          {required && <span className="fld-required">*</span>}
        </label>
      )}

      {files.length > 0 && (
        <div className="fi-files">
          {files.map((file, index) => {
            const kind = kindOf(file, allow);
            const spec = kind ? KINDS[kind] : null;
            return (
              <div className={`fi-file ${kind ? `kind-${kind}` : ""}`.trim()} key={`${file.name}-${index}`}>
                <FileIcon badge={spec?.badge || "FILE"} />
                <div className="fi-file-meta">
                  <div className="fi-file-name" title={file.name}>
                    {file.name}
                  </div>
                  <div className="fi-file-kind">
                    {spec?.label || "File"}
                    <span className="fi-dot">·</span>
                    {formatSize(file.size)}
                  </div>
                </div>
                <button
                  type="button"
                  className="fi-remove"
                  aria-label={`Remove ${file.name}`}
                  disabled={disabled}
                  onClick={() => remove(index)}
                >
                  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M4.5 4.5l7 7m0-7l-7 7"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {showEmptyState && (
        <div
          className={`fi-drop ${dragging ? "dragging" : ""}`.trim()}
          onClick={() => !disabled && inputRef.current?.click()}
          onDragOver={(event) => {
            if (disabled) return;
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            if (disabled) return;
            event.preventDefault();
            setDragging(false);
            commit(Array.from(event.dataTransfer.files || []));
          }}
        >
          <span className="fi-drop-btn">{buttonText}</span>
          <span className="fi-drop-hint">{hint} · or drop a file here</span>
        </div>
      )}

      <input
        id={generatedId}
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        hidden
        aria-invalid={error || !!rejected || undefined}
        aria-describedby={message ? helperId : undefined}
        onChange={(event) => commit(Array.from(event.target.files || []))}
      />

      {message && (
        <div className="fld-helper" id={helperId}>
          {message}
        </div>
      )}
    </div>
  );
};

export default FileInput;
